import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Bold, Code, Italic, Link2, SendHorizontal, Smile, X } from "lucide-react";
import { humanizeClientError } from "@/lib/api";
import { fetchChatGifs, type ChatGif } from "@/lib/chatConnect";
import { EMOJI_CATEGORIES, searchEmoji } from "@/lib/emoji";

export type ChatSendInput = { body: string; imageUrl?: string; imageAlt?: string };

type Popover = "emoji" | "gif" | "link" | null;

type Props = {
  placeholder: string;
  /** Resets draft + pickers when the open conversation changes. */
  conversationKey: string;
  onSend: (input: ChatSendInput) => Promise<void>;
};

export function ChatComposer({ placeholder, conversationKey, onSend }: Props) {
  const [draft, setDraft] = useState("");
  const [gif, setGif] = useState<ChatGif | null>(null);
  const [popover, setPopover] = useState<Popover>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  useEffect(() => {
    setDraft("");
    setGif(null);
    setPopover(null);
    setError("");
  }, [conversationKey]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [draft]);

  useEffect(() => {
    if (!popover) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setPopover(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [popover]);

  function rememberSelection() {
    const el = textareaRef.current;
    if (el) selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
  }

  const replaceSelection = useCallback(
    (build: (selected: string) => { text: string; cursorStart: number; cursorEnd: number }) => {
      const el = textareaRef.current;
      const { start, end } = el
        ? { start: el.selectionStart, end: el.selectionEnd }
        : selectionRef.current;
      const selected = draft.slice(start, end);
      const { text, cursorStart, cursorEnd } = build(selected);
      const next = draft.slice(0, start) + text + draft.slice(end);
      setDraft(next);
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        node.setSelectionRange(start + cursorStart, start + cursorEnd);
      });
    },
    [draft],
  );

  function wrap(marker: string) {
    replaceSelection((selected) => {
      const inner = selected || "";
      return {
        text: `${marker}${inner}${marker}`,
        cursorStart: marker.length,
        cursorEnd: marker.length + inner.length,
      };
    });
  }

  function insertText(text: string) {
    const el = textareaRef.current;
    if (el && document.activeElement !== el) {
      el.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
    }
    replaceSelection(() => ({ text, cursorStart: text.length, cursorEnd: text.length }));
  }

  function openLink() {
    rememberSelection();
    const { start, end } = selectionRef.current;
    const selected = draft.slice(start, end).trim();
    if (/^https?:\/\//i.test(selected)) {
      setLinkUrl(selected);
      setLinkLabel("");
    } else {
      setLinkUrl("");
      setLinkLabel(selected);
    }
    setPopover(popover === "link" ? null : "link");
  }

  function applyLink() {
    let url = linkUrl.trim();
    if (!url) return;
    if (!/^(https?:|mailto:)/i.test(url)) url = `https://${url}`;
    const label = linkLabel.trim().replace(/[<>|]/g, "");
    const el = textareaRef.current;
    if (el) el.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
    const token = label ? `<${url}|${label}>` : `<${url}>`;
    replaceSelection(() => ({ text: token, cursorStart: token.length, cursorEnd: token.length }));
    setPopover(null);
  }

  async function submit() {
    const body = draft.trim();
    if ((!body && !gif) || sending) return;
    setSending(true);
    setError("");
    try {
      // The GIF URL rides in the body too so an API without image blocks still posts it.
      const input: ChatSendInput = gif
        ? {
            body: body ? `${body}\n${gif.url}` : gif.url,
            imageUrl: gif.url,
            imageAlt: gif.title || "GIF",
          }
        : { body };
      await onSend(input);
      setDraft("");
      setGif(null);
      setPopover(null);
    } catch (err) {
      setError(humanizeClientError(err, "Could not send the message."));
    } finally {
      setSending(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
      return;
    }
    const mod = e.metaKey || e.ctrlKey;
    if (!mod) return;
    const key = e.key.toLowerCase();
    if (key === "b") {
      e.preventDefault();
      wrap("*");
    } else if (key === "i") {
      e.preventDefault();
      wrap("_");
    } else if (key === "k") {
      e.preventDefault();
      openLink();
    }
  }

  const canSend = Boolean(draft.trim() || gif) && !sending;

  return (
    <div className="chat-composer" ref={rootRef}>
      {popover === "emoji" ? (
        <EmojiPicker
          onPick={(char) => insertText(char)}
          onClose={() => setPopover(null)}
        />
      ) : null}
      {popover === "gif" ? (
        <GifPicker
          onPick={(picked) => {
            setGif(picked);
            setPopover(null);
            requestAnimationFrame(() => textareaRef.current?.focus());
          }}
          onClose={() => setPopover(null)}
        />
      ) : null}
      {popover === "link" ? (
        <div className="chat-popover chat-link-popover" role="dialog" aria-label="Add link">
          <label>
            <span>Link</span>
            <input
              type="url"
              autoFocus
              placeholder="https://"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === "Escape") setPopover(null);
              }}
            />
          </label>
          <label>
            <span>Text</span>
            <input
              type="text"
              placeholder="Optional"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyLink();
                } else if (e.key === "Escape") setPopover(null);
              }}
            />
          </label>
          <div className="chat-link-actions">
            <button type="button" className="secondary-btn" onClick={() => setPopover(null)}>
              Cancel
            </button>
            <button type="button" className="primary-btn" disabled={!linkUrl.trim()} onClick={applyLink}>
              Add link
            </button>
          </div>
        </div>
      ) : null}

      {gif ? (
        <div className="chat-composer-gif">
          <img src={gif.previewUrl} alt={gif.title || "GIF"} />
          <button
            type="button"
            className="chat-composer-gif-remove"
            aria-label="Remove GIF"
            onClick={() => setGif(null)}
          >
            <X size={14} />
          </button>
        </div>
      ) : null}

      <div className="chat-composer-box">
        <textarea
          ref={textareaRef}
          className="chat-composer-input"
          rows={1}
          value={draft}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={rememberSelection}
          onSelect={rememberSelection}
        />
        <div className="chat-composer-toolbar">
          <div className="chat-composer-tools">
            <button type="button" className="chat-tool-btn" title="Bold (⌘B)" aria-label="Bold" onClick={() => wrap("*")}>
              <Bold size={16} />
            </button>
            <button type="button" className="chat-tool-btn" title="Italic (⌘I)" aria-label="Italic" onClick={() => wrap("_")}>
              <Italic size={16} />
            </button>
            <button type="button" className="chat-tool-btn" title="Code" aria-label="Code" onClick={() => wrap("`")}>
              <Code size={16} />
            </button>
            <button
              type="button"
              className={`chat-tool-btn${popover === "link" ? " active" : ""}`}
              title="Link (⌘K)"
              aria-label="Link"
              onMouseDown={(e) => e.preventDefault()}
              onClick={openLink}
            >
              <Link2 size={16} />
            </button>
            <span className="chat-tool-sep" aria-hidden />
            <button
              type="button"
              className={`chat-tool-btn${popover === "emoji" ? " active" : ""}`}
              title="Emoji"
              aria-label="Emoji"
              aria-expanded={popover === "emoji"}
              onMouseDown={(e) => {
                e.preventDefault();
                rememberSelection();
              }}
              onClick={() => setPopover(popover === "emoji" ? null : "emoji")}
            >
              <Smile size={16} />
            </button>
            <button
              type="button"
              className={`chat-tool-btn chat-tool-gif${popover === "gif" ? " active" : ""}`}
              title="GIF"
              aria-label="GIF"
              aria-expanded={popover === "gif"}
              onClick={() => setPopover(popover === "gif" ? null : "gif")}
            >
              GIF
            </button>
          </div>
          <button
            type="button"
            className="chat-send-btn"
            aria-label="Send"
            title="Send (Enter)"
            disabled={!canSend}
            onClick={() => void submit()}
          >
            <SendHorizontal size={16} />
          </button>
        </div>
      </div>
      {error ? (
        <p className="chat-composer-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function EmojiPicker({ onPick, onClose }: { onPick: (char: string) => void; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(EMOJI_CATEGORIES[0]!.id);
  const results = query.trim()
    ? searchEmoji(query)
    : EMOJI_CATEGORIES.find((c) => c.id === category)?.emoji || [];

  return (
    <div className="chat-popover chat-emoji-picker" role="dialog" aria-label="Emoji">
      <input
        type="search"
        className="chat-picker-search"
        placeholder="Search emoji"
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />
      {!query.trim() ? (
        <div className="chat-emoji-tabs" role="tablist">
          {EMOJI_CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={c.id === category}
              title={c.label}
              className={c.id === category ? "active" : undefined}
              onClick={() => setCategory(c.id)}
            >
              {c.icon}
            </button>
          ))}
        </div>
      ) : null}
      <div className="chat-emoji-grid">
        {results.map((entry) => (
          <button
            key={`${entry.name}-${entry.char}`}
            type="button"
            title={`:${entry.name}:`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(entry.char)}
          >
            {entry.char}
          </button>
        ))}
        {!results.length ? <p className="muted">No emoji match.</p> : null}
      </div>
    </div>
  );
}

function GifPicker({ onPick, onClose }: { onPick: (gif: ChatGif) => void; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState<ChatGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState("");
  const requestRef = useRef(0);

  useEffect(() => {
    const id = ++requestRef.current;
    const timer = window.setTimeout(
      () => {
        setLoading(true);
        fetchChatGifs(query)
          .then((result) => {
            if (id !== requestRef.current) return;
            setGifs(result.gifs);
            setNotice(
              result.configured
                ? result.gifs.length
                  ? ""
                  : "No GIFs found."
                : result.message || "GIF search is not configured.",
            );
          })
          .catch((err) => {
            if (id !== requestRef.current) return;
            setGifs([]);
            setNotice(humanizeClientError(err, "Could not load GIFs."));
          })
          .finally(() => {
            if (id === requestRef.current) setLoading(false);
          });
      },
      query.trim() ? 350 : 0,
    );
    return () => window.clearTimeout(timer);
  }, [query]);

  return (
    <div className="chat-popover chat-gif-picker" role="dialog" aria-label="GIFs">
      <input
        type="search"
        className="chat-picker-search"
        placeholder="Search GIPHY"
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      />
      <p className="chat-gif-heading muted">{query.trim() ? "Results" : "Trending"}</p>
      <div className="chat-gif-grid">
        {gifs.map((gif) => (
          <button key={gif.id} type="button" title={gif.title} onClick={() => onPick(gif)}>
            <img src={gif.previewUrl} alt={gif.title || "GIF"} loading="lazy" />
          </button>
        ))}
      </div>
      {loading && !gifs.length ? <p className="muted chat-gif-note">Loading GIFs…</p> : null}
      {notice && !loading ? <p className="muted chat-gif-note">{notice}</p> : null}
      <p className="chat-gif-attribution">Powered by GIPHY</p>
    </div>
  );
}
